import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router";
import { useSelector } from "react-redux";
import {
  Briefcase,
  CheckCircle2,
  FolderKanban,
  User,
  Headphones,
  Wallet,
  Phone,
  AlertTriangle,
  CalendarClock,
  Clock,
  ShieldCheck,
  UserPlus,
  Users,
  // Handshake, // settlements hidden
} from "lucide-react";
import { FilterDismissLink } from "../../components/dashboard/BackNavLink";
import { personNameColumns } from "../../utils/personName";
import PageShell from "../../components/dashboard/PageShell";
import FilteredDataTable from "../../components/dashboard/FilteredDataTable";
import AssignCollectorModal from "../../components/dashboard/AssignCollectorModal";
import AssignQuantityBar from "../../components/dashboard/AssignQuantityBar";
import EscalateCaseModal from "../../components/dashboard/EscalateCaseModal";
import InitiatePaymentModal from "../../components/dashboard/InitiatePaymentModal";
import SlaTimelineDrawer from "../../components/dashboard/SlaTimelineDrawer";
import PaymentPlanModal from "../../components/dashboard/PaymentPlanModal";
import PreApprovalModal from "../../components/dashboard/PreApprovalModal";
import StatusBadge from "../../components/dashboard/StatusBadge";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import { CollectorCell } from "../../components/dashboard/tableCellContent";
import { CASES_FILTER_CONFIG } from "../../config/tableFilterConfigs";
import { USE_MOCK_AUTH } from "../../config/api";
import { MOCK_CASES, MOCK_CUSTOMERS, MOCK_PORTFOLIO_GROUPS } from "../../data/mock/dashboardData";
import useRole from "../../hooks/useRole";
import { useLiveEventTick } from "../../hooks/useLiveRefresh";
import usePermissions from "../../hooks/usePermissions";
import { OPS_PERMISSIONS as P } from "../../config/permissions";
import { ROLES } from "../../config/roles";
import { listOpsUsers } from "../../data/opsUsersStore";
import {
  assignOpsCase,
  bulkAssignOpsCases,
  fetchAssignableAgents,
  fetchOpsCaseSla,
  fetchOpsCases,
  fetchOpsCasesSummary,
  normalizeOpsCase,
} from "../../services/institutionWorkspaceApi";
import { fetchOpsPaymentPlans, fetchOpsPreApprovals } from "../../services/paymentApi";
import { showSuccess, showError } from "../../utils/toast";
import { caseAccountNumber, caseMatchesId } from "../../utils/opsCasesPath";

function latestByCase(rows, caseIdKey = "case_id") {
  const map = new Map();
  (rows || []).forEach((row) => {
    const key = String(row?.[caseIdKey] || row?.caseId || "");
    if (!key) return;
    const current = map.get(key);
    const nextTime = new Date(row.created_at || row.start_date || 0).getTime();
    const currentTime = new Date(current?.created_at || current?.start_date || 0).getTime();
    if (!current || nextTime >= currentTime) map.set(key, row);
  });
  return map;
}

function enrichMockCase(row) {
  const customer = MOCK_CUSTOMERS.find((c) => c.name === row.customer);
  const outstandingAmount = Math.max(0, row.amountOwed - row.amountPaid);
  const groupName =
    row.portfolioGroupName ||
    MOCK_PORTFOLIO_GROUPS.find((g) => g.id === row.groupId)?.name ||
    null;
  return {
    ...row,
    outstandingAmount,
    phone: customer?.phone ?? "",
    portfolioGroupName: groupName,
  };
}

function listMockAgents() {
  return listOpsUsers()
    .filter(
      (user) =>
        user.status === "active" &&
        (user.role === ROLES.CALL_CENTER_AGENT || user.role === ROLES.CALL_CENTER_MANAGER)
    )
    .map((user) => ({
      id: user.admin_slug || user.id,
      admin_slug: user.admin_slug || user.id,
      name: user.name,
      role:
        user.role === ROLES.CALL_CENTER_MANAGER ? "Call Centre Manager" : "Call Centre Agent",
      status: user.status,
      casesAssigned: MOCK_CASES.filter((c) => c.assignedAgent === user.name && c.status !== "closed")
        .length,
      contactsToday: user.contactsToday ?? 0,
      email: user.email,
    }));
}

function nextStatusAfterAssign(currentStatus) {
  const key = String(currentStatus || "open").toLowerCase();
  if (key === "closed") return currentStatus;
  if (key === "open" || key === "pending" || key === "") return "active";
  return currentStatus || "active";
}

function applyAssignmentPatch(row, agent, agentSlug, today) {
  return {
    ...row,
    assigned: agent.name,
    assignedAgent: agent.name,
    assignedAgentSlug: agentSlug,
    assignedAt: row.assignedAt || today,
    updatedAt: today,
    status: nextStatusAfterAssign(row.status),
  };
}

function nothingLeftToCollect(row) {
  const left = Number(row?.outstandingAmount ?? row?.outstanding);
  return Number.isFinite(left) && left <= 0;
}

function resolveAgent(agents, selected) {
  return agents.find(
    (agent) => agent.id === selected || agent.admin_slug === selected || agent.name === selected
  );
}

export default function CasesPage() {
  const { queue } = useParams();
  const [searchParams] = useSearchParams();
  const customerParam = searchParams.get("customer");
  const institutionParam = searchParams.get("institution");
  const groupParam = searchParams.get("group");
  const caseIdParam = searchParams.get("caseId");
  const accountParam = searchParams.get("account") || searchParams.get("account_number");
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const { isCallCenterAgent, isCallCenterManager, isExecutiveManager, isLegalOfficer, isSuperAdmin, user } = useRole();
  const canViewSlaJourney = isSuperAdmin || isExecutiveManager || isCallCenterManager;
  const { hasPermission } = usePermissions();
  const canLogInteraction = hasPermission(P.CASES_LOG_INTERACTION);
  const canAssign = hasPermission(P.ASSIGNMENT_MANAGE) || hasPermission(P.CASES_ASSIGN);
  const canViewPayments = hasPermission(P.PAYMENTS_VIEW);
  const seesOnlyAssigned =
    hasPermission(P.CASES_VIEW_ASSIGNED) &&
    !hasPermission(P.CASES_VIEW) &&
    !canAssign;
  const canInitiatePayment = hasPermission(P.PAYMENTS_INITIATE);
  // const canSettlement = hasPermission(P.SETTLEMENTS_MANAGE); // settlements hidden

  const [cases, setCases] = useState(() => (useLiveApi ? [] : MOCK_CASES.map(enrichMockCase)));
  const [mandates, setMandates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [agents, setAgents] = useState(() => (useLiveApi ? [] : listMockAgents()));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(useLiveApi);
  const [assigningCase, setAssigningCase] = useState(null);
  const [assigningGroup, setAssigningGroup] = useState(null);
  const [paymentCase, setPaymentCase] = useState(null);
  const [mandateCase, setMandateCase] = useState(null);
  const [planCase, setPlanCase] = useState(null);
  const [escalateCase, setEscalateCase] = useState(null);
  const [slaCaseId, setSlaCaseId] = useState(null);
  const loadCaseSla = useCallback((caseId) => fetchOpsCaseSla(token, caseId), [token]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [bulkRows, setBulkRows] = useState(null);
  const [listVersion, setListVersion] = useState(0);
  const liveCaseTick = useLiveEventTick(/^(portfolio\.|case\.|payment\.|payment_plan\.|preapproval\.)/);
  const assignmentQueue = queue === "assigned" || queue === "unassigned" ? queue : "";

  useEffect(() => {
    if (!useLiveApi) {
      setCases(MOCK_CASES.map(enrichMockCase));
      setLoading(false);
      return undefined;
    }

    if (!assignmentQueue) return undefined;

    let cancelled = false;
    const quiet = Boolean(liveCaseTick) || listVersion > 0;
    if (!quiet) setLoading(true);

    Promise.allSettled([
      fetchOpsCases(token, { assigned: assignmentQueue }, { force: quiet || listVersion > 0 }),
      fetchOpsCasesSummary(token, { force: quiet }),
      fetchOpsPreApprovals(token),
      fetchOpsPaymentPlans(token),
    ])
      .then(([casesResult, summaryResult, mandateResult, planResult]) => {
        if (cancelled) return;
        if (casesResult.status === "fulfilled") {
          setCases((casesResult.value || []).map((row) => normalizeOpsCase(row)).filter(Boolean));
        } else {
          showError(casesResult.reason?.message || "Could not load cases.");
        }
        if (summaryResult.status === "fulfilled") setSummary(summaryResult.value || null);
        if (mandateResult.status === "fulfilled") setMandates(mandateResult.value || []);
        if (planResult.status === "fulfilled") setPlans(planResult.value || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, useLiveApi, liveCaseTick, assignmentQueue, listVersion]);

  useEffect(() => {
    if (!useLiveApi) {
      setAgents(listMockAgents());
      return undefined;
    }
    if (!canAssign) {
      setAgents([]);
      return undefined;
    }

    let cancelled = false;
    fetchAssignableAgents(token)
      .then((agentRows) => {
        if (!cancelled) setAgents(agentRows || []);
      })
      .catch(() => {
        if (!cancelled) setAgents([]);
      });

    return () => {
      cancelled = true;
    };
  }, [token, useLiveApi, canAssign]);

  const scopedCases = useMemo(() => {
    let base = cases;
    if (isCallCenterAgent && !useLiveApi) {
      base = base.filter((c) => c.assignedAgent === user?.name);
    }
    if (assignmentQueue === "unassigned") {
      base = base.filter((c) => !c.assignedAgent);
    } else if (assignmentQueue === "assigned") {
      base = base.filter((c) => c.assignedAgent);
    }
    if (institutionParam) {
      base = base.filter(
        (c) =>
          c.institution === institutionParam ||
          String(c.provider_id ?? "") === String(institutionParam)
      );
    }
    if (groupParam) {
      const needle = String(groupParam);
      base = base.filter((c) =>
        [c.groupId, c.portfolioGroupName].some(
          (value) => value != null && String(value) === needle
        )
      );
    }

    const accountFromCase = caseIdParam
      ? caseAccountNumber(base.find((row) => caseMatchesId(row, caseIdParam)))
      : "";
    const accountNeedle = (accountParam || accountFromCase).trim().toLowerCase();
    if (accountNeedle) {
      base = base.filter((row) => caseAccountNumber(row).toLowerCase() === accountNeedle);
    }
    const mandateMap = latestByCase(mandates);
    const planMap = latestByCase(plans);
    return base.map((row) => {
      const mandate = mandateMap.get(String(row.id)) || mandateMap.get(String(row.case_id || ""));
      const plan = planMap.get(String(row.id)) || planMap.get(String(row.case_id || ""));
      return {
        ...row,
        mandateStatus: mandate?.approval_status || mandate?.status || "",
        mandateEnd: mandate?.end_date || "",
        planStatus: plan?.status || "",
        planStart: plan?.start_date || "",
        planEnd: plan?.end_date || "",
      };
    });
  }, [cases, mandates, plans, isCallCenterAgent, user?.name, institutionParam, groupParam, caseIdParam, accountParam, useLiveApi, assignmentQueue]);

  const scopedAccount = useMemo(() => {
    if (accountParam) return accountParam;
    if (caseIdParam) {
      return caseAccountNumber(cases.find((row) => caseMatchesId(row, caseIdParam)));
    }
    return "";
  }, [accountParam, caseIdParam, cases]);

  const scopedCustomer = scopedCases[0]?.customer || customerParam || "";

  const initialFilters = useMemo(() => {
    if (institutionParam) return { institution: [institutionParam] };
    return undefined;
  }, [institutionParam]);

  const isCustomerScoped = Boolean(accountParam || caseIdParam);
  const queueScoped = Boolean(assignmentQueue);
  const closedCount = isCustomerScoped || queueScoped
    ? scopedCases.filter((c) => c.status === "closed").length
    : summary?.closed_cases ?? scopedCases.filter((c) => c.status === "closed").length;
  const activeCount = isCustomerScoped || queueScoped
    ? scopedCases.filter((c) => c.status !== "closed").length
    : summary?.active_cases ?? scopedCases.filter((c) => c.status !== "closed").length;
  const totalCount = isCustomerScoped || queueScoped
    ? scopedCases.length
    : summary?.total_cases ?? scopedCases.length;

  const handleAssign = async (target = {}) => {
    if (!canAssign) {
      showError("You do not have permission to assign cases.");
      return;
    }
    const agent = resolveAgent(agents, selectedAgent);
    if (!agent) {
      showError("Select an operator who can receive assignments.");
      return;
    }

    setAssigning(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const agentSlug = agent.admin_slug || agent.id;

      if (bulkRows?.length) {
        const openRows = bulkRows.filter((row) => row.status !== "closed");
        if (!openRows.length) {
          showError("Closed cases stay archived.");
          return;
        }
        if (useLiveApi) {
          const result = await bulkAssignOpsCases(token, {
            agent_slug: agentSlug,
            case_ids: openRows.map((row) => row.id),
            ...target,
          });
          const batchNote = result.batch_codes?.length ? ` Batch ${result.batch_codes.join(", ")}.` : "";
          const verb = openRows.every((row) => row.assignedAgent) ? "reassigned" : "assigned";
          showSuccess(
            `${result.assigned_count ?? openRows.length} cases ${verb} to ${agent.name}.${batchNote}`
          );
        } else {
          const verb = openRows.every((row) => row.assignedAgent) ? "reassigned" : "assigned";
          showSuccess(`${openRows.length} cases ${verb} to ${agent.name}.`);
        }
        const ids = new Set(openRows.map((row) => row.id));
        setCases((prev) =>
          prev.map((row) => (ids.has(row.id) ? applyAssignmentPatch(row, agent, agentSlug, today) : row))
        );
        setBulkRows(null);
        setListVersion((version) => version + 1);
        return;
      }

      if (assigningGroup) {
        if (useLiveApi) {
          const result = await bulkAssignOpsCases(token, {
            agent_slug: agentSlug,
            provider_id: assigningGroup.providerId,
            provider_slug: assigningGroup.providerId,
            portfolio_group_slug: assigningGroup.groupSlug,
            ...target,
          });
          setCases((prev) =>
            prev.map((c) =>
              c.groupId === assigningGroup.groupSlug
                ? applyAssignmentPatch(c, agent, agentSlug, today)
                : c
            )
          );
          const groupBatch = result.batch_codes?.length ? ` Batch ${result.batch_codes.join(", ")}.` : "";
          showSuccess(
            `${result.assigned_count ?? assigningGroup.previewCases.length} cases in ${
              assigningGroup.name
            } assigned to ${agent.name}.${groupBatch}`
          );
        } else {
          setCases((prev) =>
            prev.map((c) =>
              c.groupId === assigningGroup.groupSlug
                ? applyAssignmentPatch(c, agent, agentSlug, today)
                : c
            )
          );
          showSuccess(
            `${assigningGroup.previewCases.length} cases in ${assigningGroup.name} assigned to ${agent.name}.`
          );
        }
        setAssigningGroup(null);
        setListVersion((version) => version + 1);
        return;
      }

      if (!assigningCase) return;
      let assigned = null;
      if (useLiveApi) {
        assigned = await assignOpsCase(token, assigningCase.id, agentSlug, target);
      }
      setCases((prev) =>
        prev.map((c) =>
          c.id === assigningCase.id ? applyAssignmentPatch(c, agent, agentSlug, today) : c
        )
      );
      setAssigningCase(null);
      setListVersion((version) => version + 1);
      const singleBatch = assigned?.batch_codes?.length ? ` Batch ${assigned.batch_codes.join(", ")}.` : "";
      showSuccess(`Case ${assigningCase.id} assigned to ${agent.name}.${singleBatch}`);
    } catch (error) {
      showError(error.message || "Could not assign case.");
    } finally {
      setAssigning(false);
    }
  };

  const openAssignGroup = (row) => {
    if (!row.groupId) {
      showError("This case is not linked to a portfolio group.");
      return;
    }
    const providerId = row.provider_id ?? row.provider_slug;
    if (useLiveApi && !providerId) {
      showError("Missing institution on this case — cannot assign the whole group.");
      return;
    }
    const previewCases = scopedCases.filter(
      (c) => c.groupId === row.groupId && c.status !== "closed"
    );
    setAssigningCase(null);
    setSelectedAgent(agents[0]?.id || "");
    setAssigningGroup({
      groupSlug: row.groupId,
      providerId: providerId,
      providerSlug: providerId,
      name: row.portfolioGroupName || row.groupId,
      previewCases: previewCases.length
        ? previewCases.map((c) => ({
            ...c,
            institution: row.portfolioGroupName || row.groupId,
            group_name: row.portfolioGroupName || row.groupId,
          }))
        : [
            {
              ...row,
              institution: row.portfolioGroupName || row.groupId,
              group_name: row.portfolioGroupName || row.groupId,
            },
          ],
    });
  };

  const columns = [
    { key: "numericId", header: "ID", type: "id" },
    { key: "updatedAt", header: "Date & time", type: "datetime" },
    ...personNameColumns(),
    { key: "institution", header: "Institution", type: "name" },
    {
      key: "portfolioGroupName",
      header: "Group / batch",
      render: (row) => (
        <span className="text-sm font-medium text-primary">
          {row.portfolioGroupName || row.groupId || "—"}
        </span>
      ),
    },
    { key: "accountNumber", header: "Account", type: "id" },
    { key: "amountOwed", header: "Amount owed", type: "currency" },
    { key: "amountPaid", header: "Amount paid", type: "currency" },
    { key: "outstandingAmount", header: "Outstanding", type: "currency" },
    {
      key: "assignedAgent",
      header: "Call center agent",
      render: (row) => <CollectorCell assignedAgent={row.assignedAgent} />,
    },
    {
      key: "status",
      header: "Status",
      type: "badge",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "mandateStatus",
      header: "Mandate",
      type: "badge",
      render: (row) =>
        row.mandateStatus ? (
          <StatusBadge status={row.mandateStatus} />
        ) : (
          <span className="text-xs text-primary/35">None</span>
        ),
    },
    {
      key: "planStatus",
      header: "Payment plan",
      type: "badge",
      render: (row) =>
        row.planStatus ? (
          <StatusBadge status={row.planStatus} />
        ) : (
          <span className="text-xs text-primary/35">None</span>
        ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "center",
      headerClassName: "text-center",
      actions: (row) => {
        const groupMates = row.groupId
          ? scopedCases.filter((c) => c.groupId === row.groupId && c.status !== "closed")
          : [];
        const groupFullyAssigned =
          groupMates.length > 0 && groupMates.every((c) => Boolean(c.assignedAgent));

        const slaAction = {
            key: "sla",
            label: "SLA journey",
            description: "Upload, assignment, first contact, and payment stages for this case",
            icon: Clock,
            tone: "sky",
            onClick: () => setSlaCaseId(row.id),
            hidden: !canViewSlaJourney || !row.assignedAgent,
          };
        const historyAction = {
            key: "history",
            label: "Contact history",
            description: seesOnlyAssigned
              ? "Your logged calls and outreach for this customer"
              : "Calls and outreach activity",
            icon: Headphones,
            tone: "sky",
            to: `/dashboard/contact-centre?customer=${encodeURIComponent(row.customer || "")}&tab=calls`,
          };
        const paymentsAction = {
            key: "payments",
            label: "View payments",
            description: seesOnlyAssigned
              ? "Payments on your assigned cases"
              : "Payment history for this case",
            icon: Wallet,
            tone: "emerald",
            to: `/dashboard/payments?account=${encodeURIComponent(caseAccountNumber(row))}&caseId=${encodeURIComponent(row.id)}`,
            hidden: !canViewPayments,
          };

        if (isExecutiveManager) {
          return [slaAction, historyAction, paymentsAction].filter((action) => !action.hidden);
        }

        return [
          slaAction,
          {
            key: "assign",
            label: row.assignedAgent ? "Reassign agent" : "Assign agent",
            description: "Route to an operator with case-work permissions",
            icon: UserPlus,
            tone: "primary",
            onClick: () => {
              setAssigningGroup(null);
              setSelectedAgent(row.assignedAgentSlug || agents[0]?.id || "");
              setAssigningCase(row);
            },
            hidden: row.status === "closed" || !canAssign || (isExecutiveManager && !row.canManageProvider),
          },
          {
            key: "assign-group",
            label: groupFullyAssigned ? "Reassign entire group" : "Assign entire group",
            description: groupFullyAssigned
              ? "Reassign every case in this portfolio group to one agent"
              : "Assign every case in this portfolio group to one agent",
            icon: Users,
            tone: "accent",
            onClick: () => openAssignGroup(row),
            hidden: row.status === "closed" || !canAssign || !row.groupId || (isExecutiveManager && !row.canManageProvider),
          },
          {
            key: "contact",
            label: "Log engagement",
            description: "Record the call, SMS, WhatsApp or email outcome",
            icon: Phone,
            tone: "primary",
            to: `/dashboard/log-contact?caseId=${encodeURIComponent(row.id)}`,
            hidden: !canLogInteraction,
          },
          historyAction,
          paymentsAction,
          {
            key: "initiate-payment",
            label: "Initiate payment",
            description: "Send MoMo prompt or card payment SMS link",
            icon: Wallet,
            tone: "emerald",
            onClick: () => setPaymentCase(row),
            hidden: row.status === "closed" || !canInitiatePayment || nothingLeftToCollect(row),
          },
          {
            key: "mandate",
            label: "Send mandate",
            description: "Ask the customer to approve a debit mandate",
            icon: ShieldCheck,
            tone: "sky",
            onClick: () => setMandateCase(row),
            hidden: row.status === "closed" || !canInitiatePayment || Boolean(row.mandateStatus) || nothingLeftToCollect(row),
          },
          {
            key: "plan",
            label: "Payment plan",
            description: "Save the instalment schedule for this case",
            icon: CalendarClock,
            tone: "accent",
            onClick: () => setPlanCase(row),
            hidden: row.status === "closed" || !canInitiatePayment || Boolean(row.planStatus) || nothingLeftToCollect(row),
          },
          {
            key: "escalate",
            label: "Escalate to manager",
            description: "Send this case to the manager who assigned it to you",
            icon: AlertTriangle,
            tone: "red",
            onClick: () => setEscalateCase(row),
            hidden: row.status === "closed" || !isCallCenterAgent || !hasPermission(P.CASES_ESCALATE),
          },
          // Settlements hidden for now — re-enable when settlement workflow ships.
          // {
          //   key: "settlement",
          //   label: "Request settlement",
          //   description: "Start a settlement or hardship request for this case",
          //   icon: Handshake,
          //   tone: "accent",
          //   to: `/dashboard/settlements/request?caseId=${encodeURIComponent(row.id)}&customer=${encodeURIComponent(row.customer || "")}`,
          //   hidden: row.status === "closed" || !canSettlement,
          // },
        ].filter((action) => !action.hidden);
      },
    },
  ];

  if (!assignmentQueue) {
    return (
      <Navigate
        to={isCallCenterAgent ? "/dashboard/cases/assigned" : "/dashboard/cases/unassigned"}
        replace
      />
    );
  }

  if (assignmentQueue === "unassigned" && isCallCenterAgent) {
    return <Navigate to="/dashboard/cases/assigned" replace />;
  }

  return (
    <PageShell
      title={assignmentQueue === "unassigned" ? "Unassigned cases" : "Assigned cases"}
      description={
        groupParam && assignmentQueue === "unassigned"
          ? "Unassigned cases from the selected portfolio group. Enter a quantity or a range, then assign them."
          : assignmentQueue === "unassigned"
          ? "Cases that still need an agent. Enter a quantity or a range, then assign them."
          : isCallCenterAgent
            ? "Your assigned caseload — log interactions and initiate payment from here."
            : "Cases already routed to a call centre agent. Enter a quantity or a range to move them to another agent."
      }
    >
      {isCustomerScoped && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/10 bg-gradient-to-r from-primary/[0.04] to-accent/[0.06] px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-primary shadow-sm">
              <User className="h-4 w-4" />
            </span>
            <div>
              <p className="text-xs font-semibold text-primary">Cases for account</p>
              <p className="text-sm font-bold text-primary">
                {scopedCustomer ? `${scopedCustomer} · ${scopedAccount}` : scopedAccount}
              </p>
            </div>
          </div>
          <FilterDismissLink fallback="/dashboard/cases" fallbackLabel="Show all cases" />
        </div>
      )}

      {groupParam && !isCustomerScoped && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/10 bg-gradient-to-r from-primary/[0.04] to-accent/[0.06] px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-primary shadow-sm">
              <FolderKanban className="h-4 w-4" />
            </span>
            <div>
              <p className="text-xs font-semibold text-primary">Portfolio group</p>
              <p className="text-sm font-bold text-primary">
                {scopedCases.find((row) => row.portfolioGroupName)?.portfolioGroupName || `Group ${groupParam}`}
              </p>
            </div>
          </div>
          <FilterDismissLink
            fallback={assignmentQueue === "assigned" ? "/dashboard/cases/assigned" : "/dashboard/cases/unassigned"}
            fallbackLabel="Show all cases"
          />
        </div>
      )}

      {institutionParam && !isCustomerScoped && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/10 bg-gradient-to-r from-primary/[0.04] to-accent/[0.06] px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-primary shadow-sm">
              <Briefcase className="h-4 w-4" />
            </span>
            <div>
              <p className="text-xs font-semibold text-primary">Cases for institution</p>
              <p className="text-sm font-bold text-primary">{institutionParam}</p>
            </div>
          </div>
          <FilterDismissLink fallback="/dashboard/cases" fallbackLabel="Show all cases" />
        </div>
      )}

      {!agents.length && canAssign && useLiveApi && !loading ? (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-900">
          No assignable operators found with case-work permissions. Grant permissions such as{" "}
          <span className="font-semibold">cases.log_interaction</span> on Team &amp; Access. You are
          excluded from the assignee list while assigning.
        </div>
      ) : null}

      <StatCardGrid cols={3}>
        <StatCard
          label={isCallCenterAgent ? "Your cases" : "Total cases"}
          value={loading ? "…" : totalCount}
          icon={FolderKanban}
          tone="primary"
        />
        <StatCard
          label="Active recovery"
          value={loading ? "…" : activeCount}
          icon={Briefcase}
          tone="accent"
        />
        <StatCard
          label="Closed (archived)"
          value={loading ? "…" : closedCount}
          icon={CheckCircle2}
          tone="emerald"
        />
      </StatCardGrid>

      <FilteredDataTable
        key={`cases-${scopedAccount || groupParam || institutionParam || "all"}-${loading ? "loading" : "ready"}`}
        data={scopedCases}
        columns={columns}
        config={{ ...CASES_FILTER_CONFIG, title: "Case filters" }}
        pageSize={10}
        initialFilters={initialFilters}
        emptyMessage={
          loading
            ? "Loading cases…"
            : assignmentQueue === "unassigned" && groupParam
              ? "No unassigned cases in this portfolio group."
              : "No cases match your filters."
        }
        bulkBar={({ filtered }) => {
          const assignableRows = isExecutiveManager
            ? filtered.filter((row) => row.canManageProvider)
            : filtered;
          return canAssign &&
            assignableRows.length > 0 &&
            (assignmentQueue === "unassigned" || assignmentQueue === "assigned") ? (
            <AssignQuantityBar
              rows={assignableRows}
              intent={assignmentQueue === "assigned" ? "reassign" : "assign"}
              disabled={!agents.length || assigning}
              onAssign={(rows) => {
                setAssigningCase(null);
                setAssigningGroup(null);
                setSelectedAgent(agents[0]?.id || "");
                setBulkRows(rows);
              }}
            />
          ) : null;
        }}
      />

      <AssignCollectorModal
        caseRow={assigningCase}
        cases={bulkRows || assigningGroup?.previewCases}
        collectors={agents}
        selectedAgent={selectedAgent}
        onSelectAgent={setSelectedAgent}
        assigning={assigning}
        onAssign={handleAssign}
        onClose={() => {
          setAssigningCase(null);
          setAssigningGroup(null);
          setBulkRows(null);
        }}
      />

      <EscalateCaseModal caseRow={escalateCase} onClose={() => setEscalateCase(null)} />

      <SlaTimelineDrawer
        open={Boolean(slaCaseId)}
        caseId={slaCaseId}
        onClose={() => setSlaCaseId(null)}
        fetchTimeline={loadCaseSla}
        portalLabel="Assigned case"
      />

      <InitiatePaymentModal
        caseRow={paymentCase}
        onClose={() => setPaymentCase(null)}
        onComplete={() => setListVersion((version) => version + 1)}
      />
      <PreApprovalModal
        caseRow={mandateCase}
        onClose={() => setMandateCase(null)}
        onComplete={() => {
          if (!useLiveApi || !token) return;
          fetchOpsPreApprovals(token, {}, { force: true }).then((rows) => setMandates(rows || []));
        }}
      />
      <PaymentPlanModal
        caseRow={planCase}
        onClose={() => setPlanCase(null)}
        onComplete={() => {
          if (!useLiveApi || !token) return;
          fetchOpsPaymentPlans(token, {}, { force: true }).then((rows) => setPlans(rows || []));
        }}
      />
    </PageShell>
  );
}
